"use client";

/**
 * AdminSelect — 검색 가능 + 선택 취소 + 가상 스크롤 셀렉트 (단일 선택)
 *
 * - searchable=true(기본): 콤보박스 — 트리거 입력창에서 바로 타이핑해 검색한다.
 * - searchable=false: 일반 셀렉트 버튼.
 * - clearable: 선택값을 X 로 초기화 (기본 true)
 * - 가상 스크롤(TanStack Virtual)로 옵션이 수천 개여도 빠르게 렌더링
 * - 옵션 image/description 지원 (상품 선택 등)
 * - 키보드: ↑/↓ 이동, Enter 선택, Esc 닫기
 *
 * 제어 컴포넌트. AdminFormField/AdminFilterField 안에 넣어 쓸 수 있다.
 *
 * @example
 * <AdminSelect options={products} value={id} onChange={setId} placeholder="상품 검색·선택" />
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Check, ChevronDown, X } from "lucide-react";

import { cn } from "../../lib/utils";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";

export interface AdminSelectOption {
  value: string;
  label: string;
  /** 옵션 앞에 표시할 이미지 URL (상품 선택 등) */
  image?: string;
  /** 라벨 아래 보조 설명 (가격/코드 등) */
  description?: string;
  disabled?: boolean;
}

export interface AdminSelectProps {
  options: AdminSelectOption[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  /** 검색(콤보박스) 사용 (기본 true) */
  searchable?: boolean;
  /** 선택 취소(X) 버튼 (기본 true) */
  clearable?: boolean;
  disabled?: boolean;
  /** 검색 결과 없을 때 문구 */
  emptyText?: string;
  /** 콤보박스 열렸을 때 입력 placeholder */
  searchPlaceholder?: string;
  id?: string;
  /** 트리거 클래스 (너비 등) */
  className?: string;
  /** 드롭다운 패널 클래스 */
  contentClassName?: string;
  /** 트리거에서 포커스가 빠질 때 (AdminFormField 의 touched 검증 트리거용) */
  onBlur?: () => void;
}

/** ref 로 노출되는 명령형 핸들 — 외부에서 강제로 포커스(+ 메뉴 열기)를 트리거할 때 사용 */
export interface AdminSelectHandle {
  focus: () => void;
}

const ROW_HEIGHT = 34;
const ROW_HEIGHT_RICH = 48; // 이미지/설명이 있을 때

const triggerBase =
  "flex h-9 w-full items-center gap-2 rounded-md border border-input bg-transparent px-3 text-sm shadow-xs outline-none transition-colors disabled:cursor-not-allowed disabled:opacity-50";
const triggerFocus =
  "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]";

export const AdminSelect = forwardRef<AdminSelectHandle, AdminSelectProps>(
  function AdminSelect(
    {
      options,
      value,
      onChange,
      placeholder = "선택하세요",
      searchable = true,
      clearable = true,
      disabled,
      emptyText = "결과가 없습니다.",
      searchPlaceholder = "검색...",
      id,
      className,
      contentClassName,
      onBlur,
    },
    ref
  ) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  // 열었을 때 아직 아무것도 타이핑 안 한 상태(false)인지, 한 번이라도 편집한 상태(true)인지.
  // false 일 때만 입력창에 현재 선택된 라벨을 보여준다 — 한 번 편집하면(빈 문자열로 지운
  // 경우 포함) 그 뒤로는 query 를 그대로 보여줘서, 검색어를 다 지웠을 때 선택값으로
  // "되돌아가는" 것처럼 보이지 않게 한다.
  const [searchTouched, setSearchTouched] = useState(false);
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  // AdminModal(Radix Dialog) 안에서 쓰일 때, Dialog 가 배경 스크롤을 잠그면서
  // (react-remove-scroll) 이 리스트는 Popover Portal 로 document.body 에 렌더되어
  // "모달 밖" 콘텐츠로 오인되어 wheel 스크롤 자체가 막힌다. 네이티브 스크롤에 기대지
  // 않고 wheel 이벤트를 직접 받아 scrollTop 을 옮겨서 우회한다 — React 의 onWheel 은
  // 기본적으로 passive 라 preventDefault 가 안 먹히므로 네이티브 리스너를 직접 붙인다.
  const handleListWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    (e.currentTarget as HTMLDivElement).scrollTop += e.deltaY;
  }, []);
  const setListRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (listRef.current) {
        listRef.current.removeEventListener("wheel", handleListWheel);
      }
      listRef.current = node;
      if (node) {
        node.addEventListener("wheel", handleListWheel, { passive: false });
      }
    },
    [handleListWheel]
  );
  const searchInputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listId = `${id ?? "admin-select"}-listbox`;

  useImperativeHandle(ref, () => ({
    focus: () => {
      if (searchable) {
        searchInputRef.current?.focus();
      } else {
        buttonRef.current?.focus();
        openMenu();
      }
    },
  }));

  const selected = options.find((o) => o.value === value);
  const rowHeight = options.some((o) => o.image || o.description)
    ? ROW_HEIGHT_RICH
    : ROW_HEIGHT;

  // 콤보박스가 열려 있고 검색어가 있을 때만 필터
  const filtered = useMemo(() => {
    if (!searchable || !open || !query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query, searchable, open]);

  // eslint-disable-next-line react-hooks/incompatible-library
  const virtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => rowHeight,
    overscan: 8,
  });

  // Radix Popover 는 내용을 Presence 로 한 프레임 늦게 마운트하는 경우가 있어서,
  // 열리는 그 순간 useVirtualizer 가 listRef.current 를 아직 null 로 보고
  // 리스트 엘리먼트를 못 찾은 채 굳어버릴 수 있다(항목이 하나도 안 보임 — 이후 아무 상태
  // 변경 없이는 다시 측정되지 않는다). 열릴 때 한 프레임 뒤에 재렌더를 강제해서
  // 그 시점엔 이미 마운트된 리스트 엘리먼트를 virtualizer 가 다시 찾게 한다.
  const [remeasureTick, forceRemeasure] = useState(0);
  useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => forceRemeasure((n) => n + 1));
    return () => cancelAnimationFrame(raf);
  }, [open]);

  // 열려있고 검색 중이 아닐 때, 현재 선택된 항목으로 active 를 맞추고 그 위치로 스크롤한다.
  // options 가 나중에(비동기) 채워지는 경우, 그리고 위 remeasureTick(리스트 엘리먼트가 늦게
  // 마운트되는 경우)에도 이 effect 가 다시 돌아서 그때 선택 항목을 찾아 스크롤해준다 —
  // "옵션 로딩 끝났는데 선택한 부분에 포커스가 안 된다"를 해결.
  // eslint-disable-next-line react-hooks/incompatible-library
  useEffect(() => {
    if (!open || query.trim()) return;
    const idx = options.findIndex((o) => o.value === value);
    if (idx >= 0) {
      setActive(idx);
      virtualizer.scrollToIndex(idx, { align: "center" });
    }
  }, [open, options, value, remeasureTick]);

  const openMenu = () => {
    if (disabled) return;
    setQuery("");
    setSearchTouched(false);
    const idx = options.findIndex((o) => o.value === value);
    setActive(idx >= 0 ? idx : 0);
    setOpen(true);
  };

  const choose = (opt: AdminSelectOption) => {
    if (opt.disabled) return;
    onChange?.(opt.value);
    setQuery("");
    setOpen(false);
  };

  const clear = () => {
    onChange?.("");
    setQuery("");
  };

  const moveActive = (delta: number) => {
    if (filtered.length === 0) return;
    const next = Math.min(Math.max(active + delta, 0), filtered.length - 1);
    setActive(next);
    virtualizer.scrollToIndex(next);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) openMenu();
      else moveActive(1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      moveActive(-1);
    } else if (e.key === "Enter") {
      if (open) {
        e.preventDefault();
        const opt = filtered[active];
        if (opt) choose(opt);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // 옵션 목록 (검색형/버튼형 공용)
  const list =
    filtered.length === 0 ? (
      <div className="px-3 py-6 text-center text-sm text-muted-foreground">
        {emptyText}
      </div>
    ) : (
      <div
        ref={setListRef}
        id={listId}
        role="listbox"
        className="max-h-60 overflow-y-auto p-1"
      >
        <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
          {virtualizer.getVirtualItems().map((row) => {
            const opt = filtered[row.index];
            const isSelected = opt.value === value;
            const isActive = row.index === active;
            return (
              <button
                key={opt.value}
                type="button"
                disabled={opt.disabled}
                // 마우스다운 시 입력창 포커스가 풀리지 않도록(콤보박스) 막는다.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(opt)}
                onMouseEnter={() => setActive(row.index)}
                className={cn(
                  "absolute left-0 flex w-full items-center gap-2 rounded-sm px-2 text-sm outline-none",
                  "disabled:pointer-events-none disabled:opacity-50",
                  isActive && "bg-accent text-accent-foreground"
                )}
                style={{ top: row.start, height: row.size }}
              >
                <Check
                  className={cn(
                    "size-4 shrink-0",
                    isSelected ? "opacity-100" : "opacity-0"
                  )}
                />
                {opt.image && (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={opt.image}
                    alt=""
                    className="size-8 shrink-0 rounded object-cover"
                  />
                )}
                <span className="min-w-0 flex-1 text-left">
                  <span className={cn("block truncate", isSelected && "font-medium")}>
                    {opt.label}
                  </span>
                  {opt.description && (
                    <span className="block truncate text-xs text-muted-foreground">
                      {opt.description}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );

  const popoverContent = (
    <PopoverContent
      align="start"
      className={cn("p-0", contentClassName)}
      style={{ width: "var(--radix-popover-trigger-width)" }}
      onKeyDown={handleKeyDown}
      // 콤보박스: 포커스를 입력창에 유지하고, 트리거 클릭은 외부닫힘에서 제외
      onOpenAutoFocus={searchable ? (e) => e.preventDefault() : undefined}
      onInteractOutside={(e) => {
        if (triggerRef.current?.contains(e.target as Node)) e.preventDefault();
      }}
    >
      {list}
    </PopoverContent>
  );

  // 검색형(콤보박스): 트리거 자리가 곧 검색 입력
  if (searchable) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <div
            ref={triggerRef}
            className={cn(
              triggerBase,
              "focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]",
              disabled && "opacity-50",
              className
            )}
          >
            {selected?.image && !open && (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={selected.image}
                alt=""
                className="size-5 shrink-0 rounded object-cover"
              />
            )}
            <input
              ref={searchInputRef}
              id={id}
              disabled={disabled}
              role="combobox"
              aria-expanded={open}
              aria-controls={listId}
              aria-autocomplete="list"
              // 열렸을 때 아직 편집 전(searchTouched=false)이면 현재 선택된 라벨을 그대로
              // 보여준다 — 열자마자 값이 사라지고 "검색..." placeholder만 보여서 뭘 골랐는지
              // 못 보는 문제 방지. 한 번 편집하면(빈 문자열로 지운 경우 포함) 그 뒤로는 query
              // 를 그대로 보여줘서, 검색어를 다 지웠을 때 선택값으로 되돌아간 것처럼 보이지 않게 한다.
              value={
                open
                  ? searchTouched
                    ? query
                    : selected?.label ?? ""
                  : selected?.label ?? ""
              }
              placeholder={open ? searchPlaceholder : placeholder}
              onChange={(e) => {
                setQuery(e.target.value);
                setSearchTouched(true);
                setActive(0);
                if (!open) setOpen(true);
              }}
              onFocus={(e) => {
                openMenu();
                e.target.select(); // 전체 선택 — 바로 타이핑하면 기존 값이 자연스럽게 교체됨
              }}
              onBlur={onBlur}
              onKeyDown={handleKeyDown}
              className="w-full min-w-0 bg-transparent outline-none placeholder:text-muted-foreground"
            />
            {clearable && selected && !disabled && (
              <button
                type="button"
                tabIndex={-1}
                aria-label="선택 해제"
                onMouseDown={(e) => e.preventDefault()}
                onClick={clear}
                className="flex shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground"
              >
                <X className="size-3.5" />
              </button>
            )}
            <ChevronDown
              className="size-4 shrink-0 text-muted-foreground"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => (open ? setOpen(false) : openMenu())}
            />
          </div>
        </PopoverAnchor>
        {popoverContent}
      </Popover>
    );
  }

  // 비검색형: 일반 셀렉트 버튼
  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) {
          const idx = options.findIndex((opt) => opt.value === value);
          setActive(idx >= 0 ? idx : 0);
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          ref={buttonRef}
          type="button"
          id={id}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          onBlur={onBlur}
          className={cn(triggerBase, triggerFocus, "justify-between", className)}
        >
          {selected ? (
            <span className="flex min-w-0 items-center gap-2">
              {selected.image && (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={selected.image}
                  alt=""
                  className="size-5 shrink-0 rounded object-cover"
                />
              )}
              <span className="truncate">{selected.label}</span>
            </span>
          ) : (
            <span className="truncate text-muted-foreground">{placeholder}</span>
          )}
          <span className="flex shrink-0 items-center gap-1">
            {clearable && selected && !disabled && (
              <span
                role="button"
                tabIndex={-1}
                aria-label="선택 해제"
                className="flex items-center justify-center rounded-sm text-muted-foreground hover:text-foreground"
                onClick={(e) => {
                  e.stopPropagation();
                  clear();
                }}
              >
                <X className="size-3.5" />
              </span>
            )}
            <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
          </span>
        </button>
      </PopoverTrigger>
      {popoverContent}
    </Popover>
  );
  }
);
